import { z } from 'zod';
import { CaregiverStatusSchema, GenderSchema } from './application.schema.js';
/** Canadian postal code: A1A 1A1 (space optional). */
export const CanadianPostalCodeSchema = z
    .string()
    .regex(/^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/, 'Invalid Canadian postal code (e.g. M5V 3A8)')
    .transform((s) => s.toUpperCase().replace(/\s+/g, ' ').trim());
/** ISO 3166-2:CA province subdivision code (2 chars, uppercased). v1 is
 *  Ontario-only per Principle 10; the field accepts any province for
 *  caregivers who live near a border but service Ontario clients. */
export const CanadianProvinceCodeSchema = z
    .string()
    .length(2)
    .transform((s) => s.toUpperCase())
    .refine((s) => /^[A-Z]{2}$/.test(s), 'Province must be a 2-letter code');
/** PATCH /api/v1/caregivers/me body — fields a caregiver can edit on themselves.
 *
 *  Notably NOT editable here:
 *  - legal name parts (change requires operator action — names must match credentials)
 *  - status, applicationSubmittedAt, activatedAt etc. (operator-only lifecycle)
 *  - SIN, EFT banking, HST info (separate /tax-info endpoint, post-activation)
 *  - operatorNotes (operator-only) */
/** Caregiver max travel radius (km), measured straight-line from the geocoded
 *  service-area (home). Integer 1–150: 150 covers an RN who'll travel far for
 *  certain shifts; 1 floor keeps it positive. The column `@default(25)` remains
 *  the value for anyone who never sets it. Shared by the caregiver self-patch +
 *  the operator backstop patch so both enforce the same bound. */
export const MaxTravelKmSchema = z.number().int().min(1).max(150);
export const CaregiverProfilePatchSchema = z.object({
    preferredName: z.string().min(1).max(120).trim().nullable().optional(),
    // Caregiver-declared travel radius (Principle 9 — the contractor's call). A
    // smaller radius means fewer shift offers; affects FUTURE offers only.
    maxTravelKm: MaxTravelKmSchema.optional(),
    phoneMobile: z.string().min(10).max(30).trim().nullable().optional(),
    phoneAlternate: z.string().min(10).max(30).trim().nullable().optional(),
    addressLine1: z.string().max(200).trim().nullable().optional(),
    addressLine2: z.string().max(200).trim().nullable().optional(),
    city: z.string().max(120).trim().nullable().optional(),
    province: CanadianProvinceCodeSchema.nullable().optional(),
    postalCode: CanadianPostalCodeSchema.nullable().optional(),
    selfDescription: z.string().max(2000).trim().nullable().optional(),
    // Self-identified — caregiver can update it (gender identity is self-determined).
    // Non-nullable value set: they may switch to PREFER_NOT_TO_SAY but not blank it.
    gender: GenderSchema.optional(),
});
/** GET /api/v1/caregivers/me response.
 *
 *  Encrypted columns (SIN, EFT) are NEVER returned — only boolean
 *  on-file flags. `operatorNotes` is NEVER returned to the caregiver. */
export const CaregiverProfileResponseSchema = z.object({
    id: z.string().uuid(),
    email: z.string().email(), // sourced from user.email
    // Names
    legalGivenName: z.string(),
    legalSurname: z.string(),
    legalMiddleNames: z.string().nullable(),
    preferredName: z.string().nullable(),
    dateOfBirth: z.string().date().nullable(),
    // Self-identified gender (nullable — pre-existing rows before the field shipped).
    gender: GenderSchema.nullable(),
    // Contact
    phoneMobile: z.string().nullable(),
    phoneAlternate: z.string().nullable(),
    // Mailing
    addressLine1: z.string().nullable(),
    addressLine2: z.string().nullable(),
    city: z.string().nullable(),
    province: z.string().nullable(),
    postalCode: z.string().nullable(),
    // Caregiver-visible profile
    selfDescription: z.string().nullable(),
    // Matching travel radius (km) — the caregiver-declared max distance from home.
    maxTravelKm: z.number().int(),
    // Tax info — booleans only, never the values
    sinOnFile: z.boolean(),
    // Tax identity is complete when EITHER a SIN (INDIVIDUAL) OR a Business Number
    // (CORPORATION) is on file — the banner/onboarding gates must use THIS, not
    // sinOnFile, or an incorporated caregiver (SIN null by design) reads as
    // permanently tax-incomplete.
    taxIdentityOnFile: z.boolean(),
    // Phase 11b-1 split — `eftOnFile` retained for backward compat (derives
    // from `eftSubmittedAt !== null` — "data on file" semantic). New
    // `eftSubmitted` + `eftVerified` expose the operator-verify lifecycle
    // explicitly so portal UI can render "submitted, pending verification"
    // vs "verified by operator." See 11b-1 lock.
    eftOnFile: z.boolean(),
    eftSubmitted: z.boolean(),
    eftVerified: z.boolean(),
    // Audit MEDIUM (2026-07-28): rejection state so the /me banner can flag
    // "action required" instead of showing the calm awaiting-verification message
    // for a rejected void cheque.
    eftRejected: z.boolean(),
    eftRejectionReason: z.string().nullable(),
    hstRegistered: z.boolean(),
    hstNumber: z.string().nullable(),
    // Lifecycle
    status: CaregiverStatusSchema,
    applicationSubmittedAt: z.string().datetime().nullable(),
    activatedAt: z.string().datetime().nullable(),
    suspendedAt: z.string().datetime().nullable(),
    // Phase 5f audit r1 — SUSPENDED status banner needs the operator's
    // recorded reason. Prisma model `Caregiver.suspensionReason` exists
    // (set by admin suspend endpoint) but wasn't returned to the
    // caregiver until 5f-a's audit closure.
    suspensionReason: z.string().nullable(),
    // Roles + skills
    roles: z.array(z.object({
        code: z.string(), // PSW / RPN / RN / COMPANION
        payRateOverride: z.number().nullable(),
    })),
    skills: z.array(z.string()), // e.g. ['FOOTCARE']
    // Voluntary immunization declaration (2026-08 vaccine revamp) — self-declared
    // codes from IMMUNIZATION_CATALOG. Optional/informational, never gates.
    immunizations: z.array(z.string()), // e.g. ['MMR', 'COVID_19']
});
/** POST /api/v1/caregivers/me/submit response.
 *  Marks the application as ready for operator review — transitions the
 *  caregiver's status from APPLYING to UNDER_REVIEW. Idempotent if
 *  already in UNDER_REVIEW; rejected if past that lifecycle point. */
export const CaregiverSubmitResponseSchema = z.object({
    status: CaregiverStatusSchema,
    applicationSubmittedAt: z.string().datetime(),
});
//# sourceMappingURL=profile.schema.js.map